import { combineReducers, configureStore } from "@reduxjs/toolkit";

import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { authApi } from "./features/auth/authService";
import authSlice from "./features/auth/authSlice";
import { availabilityApi } from "./features/availability/availabilityService";
import { bookableExperienceApi } from "./features/bookableExperience/bookableExperienceService";
import { bookingApi } from "./features/booking/bookingService";
import { businessApi } from "./features/business/businessService";
import businessSlice from "./features/business/businessSlice";
import { businessListApi } from "./features/businessList/businessListService";
import commonSlice from "./features/common/commonSlice";
import { industriesApi } from "./features/industries/industriesService";
import { influencersApi } from "./features/influencer/influencerService";
import influencerSlice from "./features/influencer/influencerSlice";
import { itemsApi } from "./features/item/itemService";

import itemsSlice from "./features/item/itemsSlice";
import messageSlice from "./features/message/messageSlice";
import { qrListApi } from "./features/qrList/qrListService";
import { referralApi } from "./features/referral/referralService";
import { regionApi } from "./features/region/regionService";
import { s3Api } from "./features/s3/s3Service";
import userSlice from "./features/user/userSlice";

import notificationSlice from "./features/notification/notificationSlice";

const persistConfig = {
  key: "root",
  storage,
  whitelist: [
    "authSlice",
    "userSlice",
    "itemsSlice",
    "browseSlice",
    "messageSlice",
    "notificationSlice",
  ],
};

const reducers = combineReducers({
  [influencersApi.reducerPath]: influencersApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [s3Api.reducerPath]: s3Api.reducer,
  [industriesApi.reducerPath]: industriesApi.reducer,
  [businessApi.reducerPath]: businessApi.reducer,
  [itemsApi.reducerPath]: itemsApi.reducer,
  [businessListApi.reducerPath]: businessListApi.reducer,
  [qrListApi.reducerPath]: qrListApi.reducer,
  [regionApi.reducerPath]: regionApi.reducer,
  [referralApi.reducerPath]: referralApi.reducer,
  [bookableExperienceApi.reducerPath]: bookableExperienceApi.reducer,
  [availabilityApi.reducerPath]: availabilityApi.reducer,
  [bookingApi.reducerPath]: bookingApi.reducer,
  influencerSlice,
  authSlice,
  userSlice,
  itemsSlice,
  commonSlice,
  businessSlice,
  messageSlice,
  notificationSlice,
});

const persistedReducer = persistReducer(persistConfig, reducers);

export const makeStore = () => {
  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware: any) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
          // Ignore Date objects in bookingService queries
          ignoredPaths: [
            'bookingService.queries',
          ],
        },
      })
        .concat(influencersApi.middleware)
        .concat(authApi.middleware)
        .concat(s3Api.middleware)
        .concat(industriesApi.middleware)
        .concat(businessApi.middleware)
        .concat(itemsApi.middleware)
        .concat(businessListApi.middleware)
        .concat(qrListApi.middleware)
        .concat(regionApi.middleware)
        .concat(referralApi.middleware)
        .concat(bookableExperienceApi.middleware)
        .concat(availabilityApi.middleware)
        .concat(bookingApi.middleware)
  });

  const persistor = persistStore(store);

  return { store, persistor };
};
// Infer the type of makeStore
export type AppStore = ReturnType<typeof makeStore>;
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<AppStore["store"]["getState"]>;
export type AppDispatch = AppStore["store"]["dispatch"];
